import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, EnvelopeSimple, InstagramLogo, FacebookLogo, LinkedinLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  "Allestimento Fiere", "Insegne Luminose", "Vetrofanie", "Decorazione Automezzi",
  "Ledwall", "Coordinato Aziendale", "Siti Web", "Progettazione Grafica", "Altro",
];

const inputCls = "bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-[#00A5C8] h-12 rounded-xl";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Richiesta inviata! Ti ricontatteremo entro 24 ore lavorative.");
      setForm({ name: "", company: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast.error("Qualcosa è andato storto. Riprova o chiamaci direttamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="py-24 lg:py-32 bg-zinc-50 border-t border-zinc-200" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-xs tracking-[0.35em] uppercase text-zinc-500 mb-6"
        >
          Contatti
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display font-semibold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl mb-16 text-zinc-900"
          style={{ wordSpacing: "0.12em" }}
          data-testid="contact-title"
        >
          Parliamo del tuo<br /><span className="text-gradient">prossimo progetto.</span>
        </motion.h2>

        <div className="grid lg:grid-cols-5 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2 flex flex-col gap-8"
          >
            <p className="text-zinc-600 leading-relaxed text-sm max-w-sm">
              Compila il modulo per un preventivo gratuito e senza impegno, oppure contattaci direttamente:
              rispondiamo entro 24 ore lavorative.
            </p>
            <div className="flex flex-col gap-5">
              <a href="tel:+393522185743" data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="p-3 rounded-xl bg-white border border-zinc-200 text-[#00A5C8]"><Phone size={22} weight="duotone" /></span>
                <span className="text-zinc-700 group-hover:text-zinc-950 transition-colors">+39 352 218 5743</span>
              </a>
              <a href="mailto:info@ig-grafiche.com" data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="p-3 rounded-xl bg-white border border-zinc-200 text-[#00A5C8]"><EnvelopeSimple size={22} weight="duotone" /></span>
                <span className="text-zinc-700 group-hover:text-zinc-950 transition-colors">info@ig-grafiche.com</span>
              </a>
            </div>
            <div className="flex gap-3" data-testid="contact-socials">
              {[
                { icon: InstagramLogo, label: "Instagram", url: "https://www.instagram.com/iggrafiche?igsi=d3k5YmFvM3dtcGsz&utm_source=qr" },
                { icon: FacebookLogo, label: "Facebook", url: "https://www.facebook.com/profile.php?id=61583916585472" },
                { icon: LinkedinLogo, label: "LinkedIn", url: "https://www.linkedin.com/company/146144953/admin/dashboard/" },
              ].map(({ icon: Icon, label, url }) => (
                <a key={label} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`social-${label.toLowerCase()}`}
                  className="p-3 rounded-full border border-zinc-300 bg-white text-zinc-600 hover:text-black hover:bg-gradient-to-r hover:from-[#00E5FF] hover:to-[#FF007F] hover:border-transparent transition-[color,background-color,border-color] duration-300">
                  <Icon size={20} weight="duotone" />
                </a>
              ))}
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-3 rounded-2xl border border-zinc-200 bg-white p-8 lg:p-10 shadow-xl shadow-zinc-200/60"
            data-testid="quote-form"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <Label htmlFor="q-name" className="text-zinc-500 text-xs uppercase tracking-wider">Nome e Cognome *</Label>
                <Input id="q-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Mario Rossi" className={inputCls} data-testid="quote-input-name" />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="q-company" className="text-zinc-500 text-xs uppercase tracking-wider">Azienda</Label>
                <Input id="q-company" value={form.company} onChange={set("company")} placeholder="Rossi S.r.l." className={inputCls} data-testid="quote-input-company" />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="q-email" className="text-zinc-500 text-xs uppercase tracking-wider">Email *</Label>
                <Input id="q-email" type="email" required value={form.email} onChange={set("email")} placeholder="mario@azienda.it" className={inputCls} data-testid="quote-input-email" />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="q-phone" className="text-zinc-500 text-xs uppercase tracking-wider">Telefono</Label>
                <Input id="q-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+39 333 000 0000" className={inputCls} data-testid="quote-input-phone" />
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <Label htmlFor="q-service" className="text-zinc-500 text-xs uppercase tracking-wider">Servizio di interesse *</Label>
                <select id="q-service" required value={form.service} onChange={set("service")} data-testid="quote-select-service"
                  className="h-12 rounded-xl bg-white border border-zinc-300 text-zinc-900 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00A5C8]">
                  <option value="" disabled>Seleziona un servizio…</option>
                  {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <Label htmlFor="q-message" className="text-zinc-500 text-xs uppercase tracking-wider">Messaggio *</Label>
                <Textarea id="q-message" required minLength={5} rows={5} value={form.message} onChange={set("message")}
                  placeholder="Raccontaci il tuo progetto: cosa ti serve, tempistiche, quantità…"
                  className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus-visible:ring-[#00A5C8] rounded-xl"
                  data-testid="quote-input-message" />
              </div>
            </div>
            <button type="submit" disabled={sending} data-testid="quote-submit-button"
              className="mt-8 group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#FF007F] text-black font-semibold text-sm tracking-wide hover:scale-[1.03] transition-transform duration-300 disabled:opacity-60 disabled:hover:scale-100 shadow-lg shadow-fuchsia-500/20">
              {sending ? "Invio in corso…" : "Invia Richiesta"}
              <PaperPlaneTilt size={18} weight="bold" className="group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
